import React, { useState, useEffect } from "react";
import {
  Mail, MailCheck, Globe, Bell, Megaphone,
  ShieldCheck, ShieldAlert, CheckCircle, XCircle,
  Pencil, Trash2, KeyRound, SendHorizonal, Loader2,
} from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import EntityFormModal from "modules/swiftboard/components/EntityFormModal";
import { useAlert } from "context/AlertContext";
import apiSendgridConfiguration from "./api";
import { configurationFields } from "./features/configuration.fields";
import { configurationValidation } from "./features/configuration.validation";
import { loadConfigurationDependencies } from "./features/configuration.dependencies";

const MAILING_FILTERS = ["ALL", "NOTIFICATIONS", "MARKETING"];

const maskApiKey = (key) => {
  if (!key) return "—";
  return `••••${key.slice(-6)}`;
};

const SendgridConfiguration = ({ onTotalCountChange }) => {
  const { showAlert } = useAlert();

  const [records,      setRecords]      = useState([]);
  const [totalCount,   setTotalCount]   = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [search,       setSearch]       = useState("");
  const [mailingFilter, setMailingFilter] = useState("ALL");
  const [currentPage,  setCurrentPage]  = useState(1);
  const [pageSize,     setPageSize]     = useState(10);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [sortConfig,   setSortConfig]   = useState({ key: "id", direction: "desc" });
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalId,      setModalId]      = useState(null);
  const [isModalOpen,  setIsModalOpen]  = useState(false);
  const [confirmData,  setConfirmData]  = useState({
    isOpen: false, title: "", message: "", confirmText: "Yes", cancelText: "Cancel", icon: null, resolve: null,
  });
  const [testMailDialog, setTestMailDialog] = useState({ isOpen: false, row: null, email: "", sending: false });

  // ── Data Fetching ──────────────────────────────────────────────────────────

  const load = async (page, size, q, mailing = mailingFilter) => {
    setLoading(true);
    try {
      const res = await apiSendgridConfiguration.getAll({
        searchText: q.trim(),
        limit: size,
        skip: (page - 1) * size,
        ...(mailing !== "ALL" && { mailing_for: mailing }),
      });
      setRecords(res.data?.data || []);
      setTotalCount(res.data?.total_count || 0);
      onTotalCountChange?.(res.data?.total_count || 0);
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Failed to load configurations." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1, pageSize, "", "ALL"); }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSearch = (val) => {
    setSearch(val);
    setCurrentPage(1);
    load(1, pageSize, val, mailingFilter);
  };

  const handleRefresh = () => {
    setSearch("");
    setCurrentPage(1);
    setMailingFilter("ALL");
    setResetTrigger((k) => k + 1);
    load(1, pageSize, "", "ALL");
  };

  const handleMailingFilter = (val) => {
    setMailingFilter(val);
    setCurrentPage(1);
    load(1, pageSize, search, val);
  };

  const handlePageChange = (page, newSize = pageSize) => {
    const s = newSize;
    const p = newSize !== pageSize ? 1 : page;
    setCurrentPage(p);
    setPageSize(s);
    load(p, s, search, mailingFilter);
  };

  const handleSort = (key) =>
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

  const handleCreate    = () => { setModalId(null); setIsModalOpen(true); };
  const handleEdit      = (id) => { setModalId(id); setIsModalOpen(true); };
  const handleCloseModal = () => { setIsModalOpen(false); setModalId(null); };

  const handleOpenTestMail = (row) => setTestMailDialog({ isOpen: true, row, email: "", sending: false });
  const handleCloseTestMail = () => setTestMailDialog({ isOpen: false, row: null, email: "", sending: false });

  const handleSendTestMail = async () => {
    const { row, email } = testMailDialog;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email.trim())) {
      showAlert({ type: "warning", message: "Please enter a valid email address." });
      return;
    }
    setTestMailDialog((p) => ({ ...p, sending: true }));
    try {
      const res = await apiSendgridConfiguration.testConfiguration(row.id, email.trim());
      if (res.data?.status) {
        showAlert({ type: "success", message: res.data.message });
        handleCloseTestMail();
      } else {
        showAlert({ type: "warning", message: res.data?.message || "Test failed." });
        setTestMailDialog((p) => ({ ...p, sending: false }));
      }
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Failed to send test email." });
      setTestMailDialog((p) => ({ ...p, sending: false }));
    }
  };

  const openConfirm = (opts) =>
    new Promise((resolve) => setConfirmData({ ...opts, isOpen: true, resolve }));

  const handleConfirmClose = (result) => {
    confirmData.resolve?.(result);
    setConfirmData((p) => ({ ...p, isOpen: false, resolve: null }));
  };

  const handleDelete = async (row) => {
    const confirmed = await openConfirm({
      title:       "Delete Configuration",
      message:     "Are you sure you want to delete this SendGrid configuration? This action cannot be undone.",
      confirmText: "Delete",
      cancelText:  "Cancel",
      variant:     "danger",
      icon:        <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      const res = await apiSendgridConfiguration.delete(row.id);
      if (res.data?.status) {
        showAlert({ type: "success", message: "SendGrid configuration deleted successfully." });
        load(currentPage, pageSize, search);
      } else {
        showAlert({ type: "warning", message: res.data?.message });
      }
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Delete failed." });
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRows.length) return;
    const confirmed = await openConfirm({
      title:       "Delete Selected",
      message:     `Delete ${selectedRows.length} configuration(s)? This cannot be undone.`,
      confirmText: "Delete All",
      cancelText:  "Cancel",
      variant:     "danger",
      icon:        <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      await Promise.all(selectedRows.map((id) => apiSendgridConfiguration.delete(id)));
      showAlert({ type: "success", message: `${selectedRows.length} configuration(s) deleted successfully.` });
      setSelectedRows([]);
      load(1, pageSize, search);
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Bulk delete failed." });
    }
  };

  // ── EntityFormModal wiring ─────────────────────────────────────────────────

  // ── Column Definitions ────────────────────────────────────────────────────

  const columns = [
    {
      key:      "id",
      label:    "ID",
      width:    "80px",
      fixed:    true,
      sortable: true,
      render: (row) => (
        <div className="font-mono text-xs font-medium text-text_primary">
          {`#SGCF${String(row.id).padStart(3, "0")}`}
        </div>
      ),
    },
    {
      key:      "from_email",
      label:    "From",
      width:    "220px",
      sortable: true,
      render: (row) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Mail size={12} className="text-text_muted flex-shrink-0" />
            <span className="text-xs font-medium text-text_primary truncate max-w-[170px]" title={row.from_email}>
              {row.from_email}
            </span>
          </div>
          {row.from_name && (
            <div className="text-[11px] text-text_muted pl-[18px]">{row.from_name}</div>
          )}
        </div>
      ),
    },
    {
      key:   "reply_to_email",
      label: "Reply-To",
      width: "200px",
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <MailCheck size={12} className="text-text_muted flex-shrink-0" />
          {row.reply_to_email ? (
            <span className="text-xs text-text_secondary truncate max-w-[160px]" title={row.reply_to_email}>
              {row.reply_to_email}
            </span>
          ) : (
            <span className="text-xs text-text_muted italic">Same as from</span>
          )}
        </div>
      ),
    },
    {
      key:   "sending_domain",
      label: "Domain",
      width: "160px",
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <Globe size={12} className="text-text_muted flex-shrink-0" />
          {row.sending_domain ? (
            <span className="text-xs text-text_primary font-medium">{row.sending_domain}</span>
          ) : (
            <span className="text-xs text-text_muted italic">Not set</span>
          )}
        </div>
      ),
    },
    {
      key:   "mailing_for",
      label: "Mailing For",
      width: "140px",
      render: (row) => {
        if (!row.mailing_for) return <span className="text-xs text-text_muted">—</span>;
        const isNotif = row.mailing_for === "NOTIFICATIONS";
        return isNotif ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-info_bg text-info rounded-lg text-xs font-medium border border-info_border">
            <Bell size={11} /> Notifications
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-secondary_tint text-secondary rounded-lg text-xs font-medium border border-secondary/20">
            <Megaphone size={11} /> Marketing
          </span>
        );
      },
    },
    {
      key:   "sendgrid_credential_fid",
      label: "Credential",
      width: "200px",
      render: (row) => {
        const cred = row.sendgrid_credential;
        if (!cred) return <span className="text-xs text-text_muted italic">Unlinked</span>;
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <KeyRound size={12} className="text-text_muted flex-shrink-0" />
              <span className="font-mono text-xs text-text_secondary tracking-wider">
                {maskApiKey(cred.api_key)}
              </span>
            </div>
            <div>
              {cred.is_domain_verified ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-success font-medium">
                  <ShieldCheck size={11} /> Domain verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] text-warning font-medium">
                  <ShieldAlert size={11} /> Unverified
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key:   "is_activated",
      label: "Status",
      width: "100px",
      render: (row) =>
        row.is_activated ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-success_bg text-success rounded-lg text-xs font-medium border border-success_border">
            <CheckCircle size={12} /> Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-surface_alt text-text_secondary rounded-lg text-xs font-medium border border-border">
            <XCircle size={12} /> Inactive
          </span>
        ),
    },
  ];

  const renderRowActions = (row) => (
    <div className="flex items-center gap-2">
      <button
        onClick={(e) => { e.stopPropagation(); handleOpenTestMail(row); }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-success_bg text-success border border-success_border hover:bg-success hover:text-white transition-all duration-200 text-xs font-semibold shadow-sm"
      >
        <SendHorizonal size={13} /> Test Mail
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); handleEdit(row.id); }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary_tint text-primary border border-primary/20 hover:bg-primary hover:text-white transition-all duration-200 text-xs font-semibold shadow-sm"
      >
        <Pencil size={13} /> Edit
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-danger_bg text-danger border border-danger_border hover:bg-danger hover:text-white transition-all duration-200 text-xs font-semibold shadow-sm"
      >
        <Trash2 size={13} /> Delete
      </button>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  const notifCount    = records.filter((r) => r.mailing_for === "NOTIFICATIONS").length;
  const marketCount   = records.filter((r) => r.mailing_for === "MARKETING").length;
  const activeCount   = records.filter((r) => r.is_activated).length;

  return (
    <div className="h-full overflow-y-auto">
    <div className="space-y-4 pb-4">

      {/* Summary Stats */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1.5 bg-primary_tint text-primary rounded-lg text-xs font-medium border border-primary/20">
          Total: {totalCount}
        </span>
        <span className="px-3 py-1.5 bg-success_bg text-success rounded-lg text-xs font-medium border border-success_border">
          Active: {activeCount}
        </span>
        <span className="px-3 py-1.5 bg-info_bg text-info rounded-lg text-xs font-medium border border-info_border">
          Notifications: {notifCount}
        </span>
        <span className="px-3 py-1.5 bg-secondary_tint text-secondary rounded-lg text-xs font-medium border border-secondary/20">
          Marketing: {marketCount}
        </span>
      </div>

      {/* Toolbar */}
      <TableToolBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onCreate={handleCreate}
        onDeleteSelected={handleBulkDelete}
        selectedCount={selectedRows.length}
        resetSearchTrigger={resetTrigger}
        canCreate
      />

      {/* Mailing For Filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-text_muted">Filter:</span>
        {MAILING_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => handleMailingFilter(f)}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              mailingFilter === f
                ? "bg-text_primary text-surface"
                : "bg-surface text-text_secondary hover:bg-surface_alt border border-border"
            }`}
          >
            {f === "ALL" ? "All" : f.charAt(0) + f.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {/* Table / Empty State */}
      {!loading && records.length === 0 ? (
        <NoDataState
          icon="file"
          title="No SendGrid Configurations"
          message={
            search || mailingFilter !== "ALL"
              ? "No configurations match your filters. Try adjusting them."
              : "Add your first SendGrid configuration to start routing emails."
          }
          showRefresh
          onRefresh={handleRefresh}
          showCreate={true}
          createText="Create New Sendgrid Configuration"
          onCreate={handleCreate}
          size="medium"
        />
      ) : (
        <>
          <div className="overflow-hidden border rounded-lg border-border">
            <TableGrid
              data={records}
              loading={loading}
              columns={columns}
              sortConfig={sortConfig}
              onSort={handleSort}
              onSelectionChange={setSelectedRows}
              showActions
              selectable
              renderActions={renderRowActions}
            />
          </div>
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            selectedCount={selectedRows.length}
          />
        </>
      )}

      {/* Create / Edit Modal */}
      <EntityFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        id={modalId}
        title="SendGrid Configuration"
        fetchById={apiSendgridConfiguration.getById}
        createFn={(data) => apiSendgridConfiguration.create(data)}
        updateFn={(id, data) => apiSendgridConfiguration.update(id, data)}
        dependenciesLoader={loadConfigurationDependencies}
        fields={configurationFields}
        validationSchema={configurationValidation}
        onSuccess={() => load(currentPage, pageSize, search)}
      />

      {/* Confirm Dialog */}
      {confirmData.isOpen && (
        <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />
      )}

      {/* Test Mail Dialog */}
      {testMailDialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-border bg-success_bg">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-success/10 border border-success_border">
                <SendHorizonal size={18} className="text-success" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-text_primary">Test Mail Configuration</h3>
                <p className="text-xs text-text_muted mt-0.5">
                  Send a test email via <span className="font-medium text-text_secondary">{testMailDialog.row?.from_email}</span>
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-text_secondary mb-1.5">
                  Recipient Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  autoFocus
                  value={testMailDialog.email}
                  onChange={(e) => setTestMailDialog((p) => ({ ...p, email: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter" && !testMailDialog.sending) handleSendTestMail(); }}
                  placeholder="you@example.com"
                  disabled={testMailDialog.sending}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-surface_alt text-text_primary placeholder:text-text_muted focus:outline-none focus:ring-2 focus:ring-success/30 focus:border-success disabled:opacity-60 transition"
                />
                <p className="text-[11px] text-text_muted mt-1.5">
                  A test email will be sent to verify this configuration is working.
                </p>
              </div>

              {testMailDialog.row?.sendgrid_credential && !testMailDialog.row.sendgrid_credential.is_domain_verified && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-warning_bg border border-warning_border rounded-lg">
                  <ShieldAlert size={14} className="text-warning mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-warning">
                    Domain is not verified. Test email may be delivered to spam or rejected.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-surface_alt">
              <button
                onClick={handleCloseTestMail}
                disabled={testMailDialog.sending}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-surface text-text_secondary border border-border hover:bg-surface_alt transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendTestMail}
                disabled={testMailDialog.sending}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-success text-white hover:bg-success/90 transition disabled:opacity-60 shadow-sm"
              >
                {testMailDialog.sending ? (
                  <><Loader2 size={13} className="animate-spin" /> Sending…</>
                ) : (
                  <><SendHorizonal size={13} /> Send Test Mail</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default SendgridConfiguration;
